import { useForm } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useLocation } from "@tanstack/react-router";
import React from "react";

import { Button, ButtonLink } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { requirePermission } from "~/lib/functions/admin-access";
import { reprovisionGhlForCoachFn } from "~/lib/functions/ghl-provisioning";
import type { EnsureGhlProvisionedResult } from "~/lib/ghl/provision-ghl-account";
import { createNoIndexMeta } from "~/lib/shared/meta";

function IntegrationsPage(): React.ReactElement {
  const location = useLocation();
  const rawSearch =
    typeof location.search === "string"
      ? location.search
      : typeof window !== "undefined"
        ? window.location.search
        : "";
  const statusParam = new URLSearchParams(rawSearch).get("status");

  const reprovision = useMutation<EnsureGhlProvisionedResult, Error, string>({
    mutationFn: (coachId) => reprovisionGhlForCoachFn({ data: { coachId } }),
  });

  const form = useForm({
    defaultValues: { coachId: "" },
    onSubmit: async ({ value }) => {
      const coachId = value.coachId.trim();
      if (!coachId) return;
      await reprovision.mutateAsync(coachId);
    },
  });

  return (
    <div className="space-y-8">
      <h1 className="font-heading text-3xl font-medium">Integrations</h1>
      {statusParam === "success" ? (
        <p className="text-green-700">Connection successful!</p>
      ) : null}

      <Card className="max-w-xl space-y-6 p-6">
        <div className="space-y-1">
          <h2 className="text-lg font-medium">GoHighLevel</h2>
          <p className="text-sm text-gray-500">
            Agency OAuth + coach sub-account provisioning.
          </p>
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-medium">Agency authorization</h3>
          <p className="text-sm text-gray-500">
            Re-authorize if the agency refresh token has been revoked — GHL
            provisioning then fails with <code>invalid_grant</code>. Sign in as
            the agency that owns the DCM company when prompted.
          </p>
          <ButtonLink href="/api/leadconnector/init/">
            Re-authorize agency app
          </ButtonLink>
        </div>

        <div className="space-y-2 border-t border-gray-200 pt-6">
          <h3 className="text-sm font-medium">Re-provision a coach</h3>
          <p className="text-sm text-gray-500">
            Retry GHL sub-account + user creation for a paid coach whose
            provisioning failed (the coach ID is in the ops alert email). No-ops
            if they already have a GHL location.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void form.handleSubmit();
            }}
            className="flex items-start gap-2"
          >
            <form.Field name="coachId">
              {(field) => (
                <Input
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="Coach ID"
                  className="max-w-xs"
                />
              )}
            </form.Field>
            <Button
              type="submit"
              variant="primary"
              loading={reprovision.isPending}
              disabled={reprovision.isPending}
            >
              Re-provision
            </Button>
          </form>

          {reprovision.isSuccess ? (
            <p className="text-sm text-green-700">
              Result: {reprovision.data.status}
              {reprovision.data.ghlLocationId
                ? ` — location ${reprovision.data.ghlLocationId}`
                : ""}
            </p>
          ) : null}
          {reprovision.isError ? (
            <p className="text-sm text-red-600">{reprovision.error.message}</p>
          ) : null}
        </div>
      </Card>
    </div>
  );
}

export const Route = createFileRoute("/admin/integrations/")({
  beforeLoad: ({ context }) =>
    requirePermission(context.adminPermissions, "integrations:read"),
  head: () =>
    createNoIndexMeta(
      "Admin - Integrations – Dream Coach Match",
      "Admin integrations and external service connections",
    ),
  component: IntegrationsPage,
});
